// サイドバー
// Drawer.tsx
import React, { useState,useEffect } from 'react';
import { styled, useTheme, Theme ,createTheme, ThemeProvider} from '@mui/material/styles';
import { useMediaQuery } from '@mui/material'; 
import MuiDrawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import ListAltIcon from '@mui/icons-material/ListAlt';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ContentPasteSearchRoundedIcon from '@mui/icons-material/ContentPasteSearchRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LinkIcon from '@mui/icons-material/Link';
import PeopleIcon from '@mui/icons-material/People';
import CategoryIcon from '@mui/icons-material/Category';
import PaletteIcon from '@mui/icons-material/Palette';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import Collapse from '@mui/material/Collapse';
import { CSSObject } from '@mui/system';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {  Paper, Box } from '@mui/material';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { fetchExternalLinks } from '../../../service/HomePage/ExternalLinkService';
import { ExternalLinkType } from './ExternalLinkType';



const drawerWidth = 370;

const openedMixin = (theme: Theme) : CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
   overflowX: 'hidden',
});

const closedMixin = (theme: Theme) : CSSObject =>  ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: "0px",
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(6)} + 1px)`,
  },
});


interface DrawerProps {
  open?: boolean;
}

const Drawer = styled(MuiDrawer, { 
  shouldForwardProp: (prop) => prop !== 'open' 
})<DrawerProps>(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open ? openedMixin(theme) : closedMixin(theme)),
  '& .MuiDrawer-paper': {
    ...(open ? openedMixin(theme) : closedMixin(theme)),
    background:theme.palette.custom.lightbluePinkGradient
  },
}));

const FireNav = styled(List)<{ component?: React.ElementType }>({
  '& .MuiListItemButton-root': {
    paddingLeft: 24,
    paddingRight: 24,
  },
  '& .MuiListItemIcon-root': {
    minWidth: 0,
    marginRight: 16,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20,
  },
});


interface DrawerComponentProps {
  open: boolean;
  handleDrawerClose: () => void;
}

/**
 * サイドバードロワーコンポーネント
 * - ナビゲーションメニュー・管理者メニュー・外部リンク一覧を表示する
 * - Drawerが閉じている状態で外部リンクをクリックするとPopoverで一覧を表示する
 * - props: open (ドロワー展開状態), handleDrawerClose (ドロワー閉じるハンドラー)
 */
export default function DrawerComponent({ open, handleDrawerClose }: DrawerComponentProps) {
  // useMediaQueryでスクリーンサイズをチェック
 const isMobile = useMediaQuery('(max-width:600px)');  // 600px以下の場合はスマートフォンデバイス
 const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: isMobile? '48px' : '56px',  
  padding: theme.spacing(0, 1),
}));

  const theme = useTheme();
  const navigate = useNavigate();
  const [externalLinksOpen, setExternalLinksOpen] = useState(true);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLDivElement | null>(null); // Popoverの位置をコントロール
  // 指定パスに遷移する
  const handleNavigation = (path: string) => {
    navigate(path);
  };
  const [externalLinks, setExternalLinks] = useState<ExternalLinkType[]>([]); // 外部リンク
  const [favicons, setFavicons] = useState<Record<string, string>>({});//リンクによってファビコンを動態的生成
  const [loadErrors, setLoadErrors] = useState<Record<string, boolean>>({}); // ファビコン獲得失敗際のリンクを記録

  // useEffect：サーバーから外部リンクデータを取得
useEffect(() => {
  const loadExternalLinks = async () => {
    const links = await fetchExternalLinks();
    setExternalLinks(links); // リンク状態更新
  };

  loadExternalLinks();
}, []);

//　useEffect：externalLinksが更新された後、urlによってfavicon mapを生成
useEffect(() => {
  const newFavicons: Record<string, string> = {};

  externalLinks.forEach(({ url }) => {
    try {
      const domain = new URL(url).hostname;
      const faviconUrl = `https://${domain}/favicon.ico`;
      newFavicons[url] = faviconUrl;
    } catch (error) {
      console.error("Invalid URL:", url);
    }
  });

  setFavicons(newFavicons);
}, [externalLinks]);

  // useEffect：Drawerが開いたとき（open === true）外部リンクリストを開く
  useEffect(() => {
      if (open) {
        setExternalLinksOpen(true);  
      }
    }, [open]);
    

  // ファビコン読み込み失敗時に該当URLをエラーとして記録する
  const handleIconError = (url: string) => {
    setLoadErrors(prev => ({ ...prev, [url]: true }));
  };

  // 外部リンクボタンクリック時：Drawer閉じ状態ならPopover表示、開き状態なら展開トグル
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!open) {
      setPopoverAnchor(event.currentTarget); // Drawerが閉じる状態でPopoverの表示位置を記録
    }
    setExternalLinksOpen(!externalLinksOpen); // 外部リンクリストを開く/閉じる
  };

  // Popoverを閉じる
  const handleClose = () => {
    setPopoverAnchor(null);
    // setExternalLinksOpen(false);
  };

  const menuItems = [
    { text: 'ホームページ', icon: <HomeIcon sx={{height:'1.2rem'}}/>, path: '/' },
    { text: '申請書一覧', icon: <ListAltIcon sx={{height:'1.2rem'}}/>, path: '/SearchWorkflow' },
    { text: '申請履歴', icon: <HourglassTopIcon sx={{height:'1.2rem'}}/>, path: '/ProgressInquiry' },
    { text: '承認履歴', icon: <HowToRegIcon sx={{height:'1.2rem'}}/>, path: '/ReviewManagement' },
    { text: '応答履歴', icon: <PendingActionsRoundedIcon sx={{height:'1.2rem'}}/>, path: '/AwaitingReport' },
    { text: '全申請一覧', icon: <ContentPasteSearchRoundedIcon sx={{height:'1.2rem'}}/>, path: '/ApplicationAudit' },
    // { text: 'ワークフロー新規作成・編集', icon: <EditNoteRoundedIcon sx={{height:'1.2rem'}}/>, path: '/WorkFlowPage' },
    // { text: 'ユーザー新規作成・編集', icon: <PeopleIcon sx={{height:'1.2rem'}}/>, path: '/UserManagement' },
    // { text: '組織体制図', icon: <AccountTreeIcon sx={{height:'1.2rem'}}/>, path: '/GroupManagement' },
  ];

  const adminMenuItems = [
    { text: 'ワークフロー新規作成・編集', icon: <EditNoteRoundedIcon sx={{height:'1.2rem'}}/>, path: '/WorkFlowPage' },
    { text: '申請種別管理', icon: <CategoryIcon sx={{height:'1.2rem'}}/>, path: '/ApplicationTypeManagement' },
    { text: 'テーマ管理', icon: <PaletteIcon sx={{height:'1.2rem'}}/>, path: '/ThemeManagement' },
    { text: 'ユーザー新規作成・編集', icon: <PeopleIcon sx={{height:'1.2rem'}}/>, path: '/UserManagement' },
    { text: '組織体制図', icon: <AccountTreeIcon sx={{height:'1.2rem'}}/>, path: '/GroupManagement' },
    { text: 'ニュース管理', icon: <NewspaperIcon sx={{height:'1.2rem'}}/>, path: '/NewsManagement' },
  ];

  const secondaryItems = [
    { text: 'マイページ', icon: <AccountCircleIcon sx={{height:'1.2rem'}}/>, path: '/MyPage' },
    // { text: '問い合わせ', icon: <HelpCenterIcon sx={{height:'1.2rem'}}/>, path: '/contact' },
  ];

  return (
    <>
    <Drawer variant="permanent"  open={open}>
      <DrawerHeader sx={{minWidth:240}}>
        <IconButton onClick={handleDrawerClose}>
          {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </DrawerHeader>
       <Divider /> 
      <List>
      {menuItems.map(({ text, icon, path }) => (
          <ListItem key={text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton sx={{ justifyContent: open ? 'flex-start' : 'center' }}
             title={!open ? text : undefined}
             onClick={() => handleNavigation(path)}>
              <ListItemIcon sx={{ justifyContent: open ? 'flex-start' : 'center' }}>
                 {icon}
              </ListItemIcon>
              <ListItemText 
              primary={
              <Typography variant='h3' sx={{ opacity: open ? 1 : 0, color:'#666666' }}> 
              {text} 
               </Typography>
              }/>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      {/* 管理者用メニュー */}
      <List>
      {adminMenuItems.map(({ text, icon, path }) => (
          <ListItem key={text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton sx={{ justifyContent: open ? 'flex-start' : 'center' }}
             title={!open ? text : undefined}
             onClick={() => handleNavigation(path)}>
              <ListItemIcon sx={{ justifyContent: open ? 'flex-start' : 'center' }}>
                 {icon}
              </ListItemIcon>
              <ListItemText 
              primary={
              <Typography variant='h3' sx={{ opacity: open ? 1 : 0, color:'#666666' }}> 
              {text} 
               </Typography>
              }/>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
      {secondaryItems.map(({ text, icon, path }) => (
          <ListItem key={text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton sx={{ justifyContent: open ? 'flex-start' : 'center' }}
            title={!open ? text : undefined}
            onClick={() => handleNavigation(path)}
            >
              <ListItemIcon sx={{ justifyContent: open ? 'flex-start' : 'center' }}>
              {icon}
              </ListItemIcon>
              <ListItemText 
              primary={
                <Typography variant='h3' sx={{ opacity: open ? 1 : 0 ,color:'#666666'}}> 
                {text} 
                 </Typography>
                }/>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        {/* 「外部リンク」ボタン */}
        <ListItem disablePadding>
          <ListItemButton sx={{ justifyContent: open ? 'flex-start' : 'center' }}
            title={!open ? '外部リンク' : undefined}
            onClick={handleClick}>
            <ListItemIcon sx={{ justifyContent: open ? 'flex-start' : 'center' }}> 
              <LinkIcon sx={{ height: '1.2rem' }} />
            </ListItemIcon>
            <ListItemText  primary={
            <Typography variant="h3" sx={{ opacity: open ? 1 : 0, color: "#666666" }}>
              外部リンク
            </Typography>}/>
          </ListItemButton>
        </ListItem>

        {/* 外部リンクリスト（ Drawerを開く際に表示される）*/}
        <Collapse in={open && externalLinksOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {externalLinks.map(({ id, linkName, url }) => (
              <ListItem key={id} disablePadding>
                <ListItemButton component="a" href={url} target="_blank">
                <ListItemIcon sx={{pl:3,ml:1}} >
                {favicons[url] && !loadErrors[url] ? (
                    <img
                      src={favicons[url]}
                      alt={linkName}
                      width="15"
                      height="15"
                      onError={() => {
                        console.log(`Failed to load favicon for ${url}`);
                        handleIconError(url);
                      }}
                    />
                  ) : (
                    <LanguageOutlinedIcon sx={{ fontSize: 15, color: '#666666' }} />
                  )}
                </ListItemIcon>
                  <ListItemText  primary={
                    <Typography variant='h3' 
                    sx={{ display: open ? 'block' : 'none'  ,color:'#666666',}}> 
                    {linkName} 
                      </Typography>
                    } />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          </Collapse>
        </List>
    </Drawer>

     {/* Popover（Drawerが閉じる状態で表示される外部リンクリスト）*/}
     <Popover
     open={Boolean(popoverAnchor)}
     anchorEl={popoverAnchor}
     onClose={handleClose}
     anchorOrigin={{
       vertical: 'bottom',
       horizontal: 'left',
     }}
     transformOrigin={{
       vertical: 'top',
       horizontal: 'left',
     }}
   >
    <ThemeProvider
          theme={createTheme({
            components: {
              MuiListItemButton: {
                defaultProps: { disableTouchRipple: true },
              },
            },
            palette: {
              mode: 'light',
              primary: { main: 'rgb(102, 157, 246)' },
              background: { paper: theme.palette.common.white },
            },
          })}
        >
          <Paper elevation={0} sx={{ maxWidth: 256 }}>
            <FireNav component="nav" disablePadding>
            <ListItem>
                <ListItemText 
                 sx={{ px: 4}}
                primary={
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#666666',     
                      fontWeight: 'medium',
                    }}
                  >
                    外部リンク
                  </Typography>
                }
                />
                </ListItem>
              <Divider />

              {/*外部リンクリスト */}
              <Box sx={{ bgcolor: 'rgba(245, 245, 245, 1)', pb: 1}}>
                {externalLinks.map(({  id, linkName, url }) => (
                  <ListItemButton key={id} component="a" href={url} target="_blank" sx={{ py:1 }}>
                    <ListItemIcon sx={{ height: '1.2rem',mr: 0 }}>
                    {
                  loadErrors[url] ? (
                    <LanguageOutlinedIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <img
                      src={favicons[url]}
                      alt={linkName}
                      width="20"
                      height="20"
                      onError={() => {
                        console.log(`Failed to load favicon for ${url}`);
                        handleIconError(url);
                      }}
                    />
                  )
                }
                    </ListItemIcon>
                    <ListItemText 
                    primary={
                      <Typography  
                      variant='body1'
                      sx={{color:'#333333',pl:1, fontWeight: 'medium'}}> 
                      {linkName} 
                       </Typography>
                      } 
                     />
                  </ListItemButton>
                ))}
              </Box>
            </FireNav>
          </Paper>
        </ThemeProvider>
      </Popover>
   </>
  );
}